import type { ButtonHTMLAttributes, ReactNode } from 'react';

import styles from './Button.module.css';
import { SpinnerIcon } from './icons';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /**
   * Shows a spinner, sets `aria-busy`, and blocks activation.
   * Design addon §8.9 — an in-flight action must be visibly in flight.
   */
  readonly loading?: boolean;
  /** Announced while loading. Screen readers get this instead of silence. */
  readonly loadingLabel?: string;
  readonly fullWidth?: boolean;
  readonly startIcon?: ReactNode;
  readonly children: ReactNode;
}

/**
 * Primary interactive control — design addon §8.1.
 *
 * `type` defaults to `button`: the HTML default is `submit`, which silently
 * submits the surrounding form and is almost never what a caller means.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingLabel = 'جارٍ التنفيذ…',
  fullWidth = false,
  startIcon,
  disabled = false,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    styles.button,
    styles[variant],
    size === 'md' ? undefined : styles[size],
    fullWidth ? styles.fullWidth : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...rest}
      type={type}
      className={classes}
      // A loading button stays focusable but must not fire again.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <>
          <SpinnerIcon className={styles.spinner} />
          <span className="visually-hidden">{loadingLabel}</span>
        </>
      ) : startIcon ? (
        <span className={styles.icon}>{startIcon}</span>
      ) : null}
      <span>{children}</span>
    </button>
  );
}
